<script lang="ts">
  import { onMount } from 'svelte';
  import { RadioButtonGroup } from '../common';
  import { getUserPreferences, updatePreference, type InfoDensity } from '../../lib/userPreferences';
  import { resetOnboarding } from '../../lib/onboardingState';

  let preferences = $state(getUserPreferences());

  // PWA Install state
  let deferredPrompt: any = null;
  let canInstall = $state(false);
  let isInstalled = $state(false);
  let isInstalling = $state(false);

  // Check if onboarding feature flag is active
  let showOnboardingSection = $state(false);

  function handleDensityChange(value: string) {
    updatePreference('infoDensity', value as InfoDensity);
    preferences = getUserPreferences();

    // Dispatch event so other components can react
    window.dispatchEvent(new CustomEvent('preferences-updated'));
  }

  onMount(() => {
    // Check for onboarding feature flag in URL - only show section if flag is present
    const urlParams = new URLSearchParams(window.location.search);
    const onboardingParam = urlParams.get('onboarding');

    // Show onboarding section ONLY if feature flag is present
    if (onboardingParam !== null) {
      showOnboardingSection = true;
    }

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      isInstalled = true;
      canInstall = false;
    }

    // Listen for install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;
      canInstall = true;
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      isInstalled = true;
      canInstall = false;
      deferredPrompt = null;
    });
  });

  async function installPWA() {
    if (!deferredPrompt) return;

    isInstalling = true;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('PWA installed');
        isInstalled = true;
        canInstall = false;
      }
    } catch (error) {
      console.error('Install error:', error);
    } finally {
      deferredPrompt = null;
      isInstalling = false;
    }
  }

  function handleRestartOnboarding() {
    if (confirm('This will reset your onboarding progress and show the welcome screen again. Continue?')) {
      resetOnboarding();
      // Reload the page to trigger onboarding
      window.location.reload();
    }
  }
</script>

<div class="preferences-tab">
  <div class="section">
    <h2>Display Preferences</h2>
    <p class="section-description">Customize how information is displayed throughout the app</p>

    <div class="preference-group">
      <label class="preference-label">
        Information Density
        <span class="help-text">Control spacing and text size across the app</span>
      </label>
      <RadioButtonGroup
        value={preferences.infoDensity}
        options={[
          {
            value: 'compact',
            label: 'Compact',
            description: 'Tighter spacing, smaller text, see more at once'
          },
          {
            value: 'comfortable',
            label: 'Comfortable',
            description: 'Balanced spacing and text size (default)'
          },
          {
            value: 'spacious',
            label: 'Spacious',
            description: 'More spacing, larger text, easier to read'
          }
        ]}
        name="info-density"
        layout="vertical"
        variant="card"
        onchange={handleDensityChange}
      />
    </div>
  </div>

  <!-- PWA Installation Section -->
  <div class="section">
    <h2>Install App</h2>
    <p class="section-description">Install Keep Finance on your device for quick access and offline use</p>

    <div class="install-container">
      {#if isInstalled}
        <div class="install-status installed">
          <div class="status-icon">✓</div>
          <div class="status-content">
            <h3>App Installed</h3>
            <p>Keep Finance is installed on your device. You can launch it from your home screen or app launcher.</p>
          </div>
        </div>
      {:else if canInstall}
        <div class="install-status can-install">
          <div class="status-icon">📱</div>
          <div class="status-content">
            <h3>Install Keep Finance</h3>
            <p>Install this app on your device for:</p>
            <ul>
              <li>Quick access from your home screen</li>
              <li>Offline functionality</li>
              <li>Native app experience</li>
              <li>No app store required</li>
            </ul>
            <button
              class="btn btn-primary install-button"
              onclick={installPWA}
              disabled={isInstalling}
            >
              {isInstalling ? 'Installing...' : 'Install App'}
            </button>
          </div>
        </div>
      {:else}
        <div class="install-status unavailable">
          <div class="status-icon">ℹ️</div>
          <div class="status-content">
            <h3>Installation Not Available</h3>
            <p><strong>Note:</strong> PWA installation is not available in development mode (localhost). To test installation:</p>
            <ol>
              <li>Build the production version: <code>npm run build</code></li>
              <li>Preview it: <code>npm run preview</code></li>
              <li>Open the preview URL in Chrome/Edge</li>
              <li>Look for the install icon (⊕) in the address bar</li>
            </ol>
            <p class="browser-instructions"><strong>Or install manually:</strong></p>
            <ul>
              <li><strong>Chrome/Edge:</strong> Menu (⋮) → Install [App Name]</li>
              <li><strong>Safari iOS:</strong> Share → Add to Home Screen</li>
              <li><strong>Safari macOS:</strong> File → Add to Dock</li>
            </ul>
          </div>
        </div>
      {/if}
    </div>
  </div>

  <!-- Onboarding Section - hidden when feature flag is active -->
  {#if showOnboardingSection}
    <div class="section">
      <h2>Onboarding</h2>
      <p class="section-description">Reset your onboarding progress and see the welcome tour again</p>

      <div class="preference-group">
        <div class="onboarding-content">
          <div class="onboarding-info">
            <h3>Restart Onboarding Tour</h3>
            <p>If you skipped the initial tour or want to see it again, you can restart the onboarding experience. This will show the welcome screen and guided setup wizard.</p>
          </div>
          <button
            class="btn btn-secondary restart-button"
            onclick={handleRestartOnboarding}
          >
            Restart Onboarding
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .preferences-tab {
    max-width: 800px;
  }

  .section {
    margin-bottom: var(--spacing-4xl);
  }

  .section h2 {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-xs) 0;
  }

  .section-description {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
    margin: 0 0 var(--spacing-2xl) 0;
  }

  .preference-group {
    margin-bottom: var(--spacing-2xl);
    padding: var(--spacing-xl);
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
  }

  .preference-label {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin-bottom: var(--spacing-lg);
  }

  .help-text {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-normal);
    color: var(--color-text-tertiary);
  }

  /* PWA Install Section */
  .install-container {
    max-width: 100%;
  }

  .install-status {
    display: flex;
    gap: var(--spacing-lg);
    padding: var(--spacing-xl);
    border-radius: var(--radius-lg);
    border: 2px solid var(--color-border);
    background: var(--color-background);
  }

  .install-status.installed {
    border-color: var(--color-success);
    background: var(--color-success-light);
  }

  .install-status.can-install {
    border-color: var(--color-primary);
    background: var(--color-primary-light);
  }

  .install-status.unavailable {
    border-color: var(--color-border);
    background: var(--color-background-secondary);
  }

  .status-icon {
    font-size: var(--font-size-4xl);
    flex-shrink: 0;
    line-height: 1;
  }

  .status-content {
    flex: 1;
  }

  .status-content h3 {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-sm) 0;
  }

  .status-content p {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    margin: 0 0 var(--spacing-md) 0;
    line-height: var(--line-height-normal);
  }

  .status-content ul,
  .status-content ol {
    margin: var(--spacing-md) 0;
    padding-left: var(--spacing-xl);
  }

  .status-content li {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-relaxed);
    margin-bottom: var(--spacing-xs);
  }

  .status-content code {
    font-family: var(--font-family-mono);
    font-size: var(--font-size-xs);
    background: var(--color-background-tertiary);
    padding: 2px 6px;
    border-radius: var(--radius-sm);
    color: var(--color-text-primary);
  }

  .browser-instructions {
    margin-top: var(--spacing-lg);
    padding-top: var(--spacing-md);
    border-top: 1px solid var(--color-border);
  }

  .status-content li strong {
    color: var(--color-text-primary);
    font-weight: var(--font-weight-semibold);
  }

  .note {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    font-style: italic;
    margin-top: var(--spacing-md);
  }

  .install-button {
    margin-top: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-2xl);
  }

  /* Onboarding Section */
  .onboarding-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-lg);
  }

  .onboarding-info {
    flex: 1;
  }

  .onboarding-info h3 {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    margin: 0 0 var(--spacing-sm) 0;
  }

  .onboarding-info p {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    margin: 0;
    line-height: var(--line-height-normal);
  }

  .restart-button {
    flex-shrink: 0;
    padding: var(--spacing-sm) var(--spacing-xl);
    white-space: nowrap;
  }

  @media (max-width: 640px) {
    .install-status {
      flex-direction: column;
      gap: var(--spacing-md);
      text-align: center;
    }

    .status-icon {
      font-size: var(--font-size-3xl);
    }

    .status-content ul {
      text-align: left;
    }

    .install-button {
      width: 100%;
    }

    .onboarding-content {
      flex-direction: column;
      align-items: stretch;
    }

    .restart-button {
      width: 100%;
    }
  }
</style>
